export const WEB_AGENT_ACTION_TYPE = 'web_agent';

export const WEB_AGENT_STEP_ACTIONS = [
  'goto', 'click', 'fill', 'select', 'press', 'waitFor', 'switchTab', 'captureHtml', 'captureBrowserData', 'screenshot', 'store', 'forEach',
] as const;

export type WebAgentStepAction = (typeof WEB_AGENT_STEP_ACTIONS)[number];
export type WebAgentBrowserDataType = 'all' | 'cookies' | 'localStorage' | 'sessionStorage';

export interface WebAgentStepTarget { selector?: string; role?: string; name?: string }

export interface WebAgentStep {
  id: string;
  action: WebAgentStepAction;
  target?: WebAgentStepTarget;
  timeoutMs?: number;
  description?: string;
  /**
   * Text for most actions. For waitFor: a number of seconds — max wait when a selector is set
   * (returns as soon as the element appears), or a fixed sleep when there is no selector.
   */
  value?: string | number;
  values?: string[];
  submit?: boolean;
  /**
   * click, select, or press. When true, this action opens a new browser tab (not a tab inside
   * the page). The next switchTab "last" then waits up to 30 seconds for that tab.
   * When omitted, that switch uses the browser tab already open.
   */
  opensNewTab?: boolean;
  /**
   * For `store`: the variable name under which the extracted page text is stored during execution
   * so that later steps can reference it as `{name}` in their value field.
   * For `captureHtml`: deprecated — runner merges fields into extracted top-level.
   */
  as?: string;
  /**
   * store only. When set, split the extracted text on whitespace/colons and keep only the
   * first or last token (e.g. "TYPE: VSC" → "TYPE" | "VSC"). Omit to store the full text.
   */
  split?: 'first' | 'last';
  optional?: boolean;
  /**
   * Skipped when the run reuses a browser that already completed this step — a sign-in a live converse
   * only needs on its first message. Ignored when the browser is new (see `sessionId` on execute).
   */
  skipOnRepeat?: boolean;
  /**
   * captureHtml only. Opt-in: when true, download linked files under the capture target.
   * When false or omitted, skip downloads.
   */
  downloadAttachments?: boolean;
  /**
   * captureHtml + downloadAttachments. Optional CSS selector(s) inside each attachment's row.
   * Separate alternatives with ` || `. With `attachmentFilterValue`, only matching rows' files
   * download. Both empty (default) → download every recognized attachment as today.
   */
  attachmentFilterSelector?: string;
  /**
   * captureHtml + downloadAttachments. Exact visible text(s) a row filter selector must match.
   * Separate alternatives with ` || `. Applied only when both this and
   * `attachmentFilterSelector` are non-empty.
   */
  attachmentFilterValue?: string;
  /**
   * captureHtml only. When true, capture the target, advance via `nextPageSelector`, and repeat
   * until the last page. Captured list fields are concatenated. When `nextPageSelector` is empty,
   * only page 1 is captured. If advance fails, page 1 is kept and a warning is returned.
   */
  traversePages?: boolean;
  /**
   * captureHtml + traversePages: next-button selector, or a numbered page panel
   * (e.g. `#paginator__pages_section`) for `.page-circle` pagination. When empty with
   * traversePages, only page 1 is captured (no auto-detect / wait).
   */
  nextPageSelector?: string;
  /**
   * If set to an earlier step id, this step is skipped when that step succeeded.
   * For a `captureHtml` dependency, skip only when that capture produced non-empty JSON.
   */
  skipWhenStepId?: string;
  /**
   * forEach only. Runs once per object in the list named by `value` (a command-form list).
   * Inside a nested step, `${item.Field}` is that object's field and `${index}` is 0, 1, 2…
   * A selector that does not start with `#` is limited to that item's row (`rowSelector`).
   */
  nestedSteps?: WebAgentStep[];
  /** forEach only. CSS for one row, e.g. `#cccTable tr.cccRow`. */
  rowSelector?: string;
  /** forEach only. Clicked until the page has one row per list item. The first row is already there. */
  addSelector?: string;
  /** forEach only. Another forEach step. This step's list stays the same length as that step's list. */
  dependsOnStepId?: string;
  /**
   * captureHtml only. Column header labels to keep (e.g. `["Claim #"]` or `"Claim #"`).
   * Values are returned as top-level arrays and concatenated across pages when traversePages is on.
   */
  extractColumns?: string | string[];
  /** @deprecated Prefer omitting — traverse runs until the last page. Kept as an optional safety cap. */
  maxPages?: number;
  /** captureBrowserData only. Omitted or `all` captures cookies and both Web Storage scopes. */
  browserDataTypes?: WebAgentBrowserDataType[];
  /**
   * captureBrowserData only. Optional exact cookie/storage key names to keep.
   * Omit or leave empty to capture every available key in the selected scopes.
   */
  browserDataFields?: string[];
}

/** Post-processing instruction; ops: rename | merge (see customInstructions.mjs). */
export interface WebAgentCustomInstruction {
  op: string;
  from?: string;
  to?: string;
  match?: 'exact' | 'prefix' | 'suffix' | 'contains' | 'regex';
  sources?: string[];
  into?: string;
  as?: 'list' | 'object';
  [key: string]: unknown;
}

export interface WebAgentSpec { startUrl?: string; steps: WebAgentStep[]; custom?: WebAgentCustomInstruction[] }

export interface WebAgentStepLogEntry {
  id: string;
  action: string;
  ok: boolean;
  /** True when this run did not perform the action (desired state already in place). */
  skipped?: boolean;
  message?: string;
  durationMs?: number;
}

/**
 * A downloadable file from `captureHtml`. When the runner has apiBase+token it uploads to S3 before
 * `done`, injects `s3_keys` into extracted, and omits base64/`mediaUrl` bytes from the stream.
 */
export interface WebAgentAttachment {
  url: string;
  filename: string;
  /** Legacy/raw base64 value. Optional once the runner has uploaded to S3. */
  data?: string;
  /** Token-bound runner URL used when bytes were not uploaded server-side. */
  mediaUrl?: string;
  contentType?: string;
  /** Set when the runner successfully uploaded this file to S3. */
  s3_key?: string;
}

export interface WebAgentExecuteResult {
  status: 'success' | 'error';
  extracted: Record<string, unknown>;
  stepLog: WebAgentStepLogEntry[];
  logs: string[];
  screenshots: string[];
  attachments?: WebAgentAttachment[];
  /**
   * Soft failures that did not abort the run (e.g. traverse stopped after page 1 because the next
   * page selector did not advance).
   */
  warnings?: string[];
  /**
   * Set when the runner kept the browser for the caller's next message. Send it back as `sessionHandle`
   * to resume that same browser, and release it with `closeWebAgentSession` when the view closes.
   */
  sessionHandle?: string;
  error: { step?: string; message: string } | null;
}
